import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, GlobeIcon, Loader2Icon, MapPinIcon, PackageIcon, PauseIcon, PlayIcon, RefreshCwIcon, SearchIcon, XIcon } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { MetricsDateFilter } from '../../components/admin/MetricsDateFilter';
import { WebOrderDetailModal } from '../../components/admin/express/WebOrderDetailModal';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { useTenantSlug } from '../../lib/use-tenant-slug';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenant } from '../../lib/tenant-context';
import { cn, formatCurrency } from '../../lib/utils';
import { canSeeExpressMoney, isVoidedStatus, markWebOrderReady, todayCrIso, useActiveExpressAssignments, useWebOrders } from '../../lib/express-hooks';

// ──────────────────────────────────────────────────────────────────
// Pedidos Web — Emilius Pizza (id 27).
//
// Lista ÚNICAMENTE los pedidos que entran por el menú público
// (/r/emilius-pizza). El filtro es source='web', el valor explícito que
// escribe el checkout público: mesero escribe 'waiter' y la venta
// rápida de caja escribe 'pos_quick_sale', así que no hay mezcla.
// Nada se deduce por costo de envío, dirección ni teléfono.
// ──────────────────────────────────────────────────────────────────

const fmtCR = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('es-CR', {
      timeZone: 'America/Costa_Rica',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch {
    return iso;
  }
};

/** Estados que significan "ya no hay nada que hacer con este pedido". */
const isDoneStatus = (status: string | null) => ['ready', 'out_for_delivery', 'delivered', 'completed'].includes(String(status || '').toLowerCase());
const deliveryLabel = (type: string | null) => {
  switch (type) {
    case 'delivery':
      return 'Envío';
    case 'pickup':
      return 'Recoger';
    case 'dine_in':
      return 'En sitio';
    default:
      return type || '—';
  }
};
export function WebOrdersPage() {
  const slug = useTenantSlug();
  const tenantCtx = useTenant();
  const {
    restaurant,
    settings,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const [startDate, setStartDate] = useState(todayCrIso);
  const [endDate, setEndDate] = useState(todayCrIso);
  const [search, setSearch] = useState('');
  // Pedido abierto en el detalle (solo lectura).
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [markingReady, setMarkingReady] = useState<number | null>(null);
  const [readyError, setReadyError] = useState<string | null>(null);
  // ─── Cocina activa / pausada ──────────────────────────────────────
  // Mismo control que vive en el Dashboard, traído acá porque Emilius
  // ya no tiene el Dashboard en su menú. Usa exactamente la misma
  // función compartida (lib/kitchen-toggle), así que el efecto sobre el
  // menú público es idéntico. Solo administración lo ve.
  const [kitchenPaused, setKitchenPaused] = useState(false);
  const [togglingKitchen, setTogglingKitchen] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  useEffect(() => {
    if (settings) setKitchenPaused(settings.kitchen_paused);
  }, [settings]);
  const handleToggleKitchen = async () => {
    if (!restaurant) return;
    setTogglingKitchen(true);
    setToggleError(null);
    const {
      toggleKitchenPause
    } = await import('../../lib/kitchen-toggle');
    const result = await toggleKitchenPause(restaurant.id, !kitchenPaused);
    setKitchenPaused(result.newValue);
    if (!result.success) {
      setToggleError(result.error || 'No se pudo actualizar el estado de cocina');
    }
    setTogglingKitchen(false);
  };
  const isEmilius = String(restaurant?.id) === '27' || restaurant?.slug === 'emilius-pizza';
  // Solo administración ve cifras de dinero. El resto de roles ve la
  // operación del pedido (número, hora, cliente, dirección, estado).
  const canSeeMoney = canSeeExpressMoney(tenantCtx?.role);
  const {
    orders,
    loading,
    error,
    refetch
  } = useWebOrders(isEmilius ? restaurant?.id : undefined, startDate, endDate);

  // Repartidor asignado por pedido. Un pedido con repartidor ya salió.
  const assignmentsByOrder = useActiveExpressAssignments(isEmilius ? restaurant?.id : undefined, isEmilius);
  const visibleOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = !q ? orders : orders.filter(o => {
      const number = String(o.order_number || o.id).toLowerCase();
      const name = (o.customer_name || '').toLowerCase();
      const phone = (o.customer_phone || '').toLowerCase();
      return number.includes(q) || name.includes(q) || phone.includes(q);
    });

    // Prioridad: lo que falta atender arriba, lo resuelto abajo.
    //   0 · pendiente  → envío sin repartidor, o recoger/local sin listo
    //   1 · resuelto   → ya tiene repartidor (enviado) o está listo
    //   2 · anulado
    const rank = (o: (typeof orders)[number]) => {
      if (isVoidedStatus(o.status)) return 2;
      if (o.delivery_type === 'delivery') {
        return assignmentsByOrder.has(Number(o.id)) ? 1 : 0;
      }
      return isDoneStatus(o.status) ? 1 : 0;
    };
    return [...filtered].sort((a, b) => {
      const diff = rank(a) - rank(b);
      if (diff !== 0) return diff;
      // Dentro del mismo grupo, el más reciente primero.
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [orders, search, assignmentsByOrder]);
  const handleMarkReady = async (orderId: number) => {
    setMarkingReady(orderId);
    setReadyError(null);
    const result = await markWebOrderReady(orderId);
    setMarkingReady(null);
    if (!result.success) {
      setReadyError(result.error || 'No se pudo marcar el pedido como listo.');
      return;
    }
    refetch();
  };
  const summary = useMemo(() => {
    const live = visibleOrders.filter(o => !isVoidedStatus(o.status));
    return {
      count: live.length,
      revenue: live.reduce((s, o) => s + o.total, 0),
      deliveries: live.filter(o => o.delivery_type === 'delivery').length
    };
  }, [visibleOrders]);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen w-full bg-gray-50 flex items-center justify-center" data-id="element-7034" data-mp="2afv62srnxxq8">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-7035" data-mp="2ogz8rb6sgs0e" />
      </div>;
  }
  if (!isEmilius) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-7036" data-mp="2kg5l8ix9df81">
        <div className="p-6 flex flex-col items-center justify-center min-h-[50vh] text-center" data-id="element-7037" data-mp="u2ybuvusuf2y">
          <GlobeIcon className="w-16 h-16 text-gray-300 mb-4" data-id="element-7038" data-mp="q2w72dtrp5jg" />
          <h1 className="text-xl font-bold text-gray-900 mb-2" data-id="element-7039" data-mp="fwwchyaa5lfz">
            Módulo no disponible
          </h1>
          <p className="text-gray-500" data-id="element-7040" data-mp="jxoogzaje4fy">
            Esta sección es exclusiva para Emilius Pizza.
          </p>
        </div>
      </AdminLayout>;
  }
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-7041" data-mp="b668ophi5qne">
      <main className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4 w-full" data-id="element-7042" data-mp="3se9xgg364xj2">
        <header className="flex flex-wrap items-start justify-between gap-4" data-id="element-7043" data-mp="188gxs0fpbq1">
          <div data-id="element-7044" data-mp="165n0ww5uyg1">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-7045" data-mp="3jdv20nwhq21u">Pedidos Web</h1>
            <p className="text-sm text-gray-500" data-id="element-7046" data-mp="3jfy1dyjj97j8">
              Pedidos que entran desde el menú público · se actualiza solo
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-7047" data-mp="1hnjl13wn0b4x">
            <button onClick={refetch} disabled={loading} className="h-10 px-4 rounded-xl font-bold text-sm bg-white border border-gray-300 text-gray-700 hover:border-gray-400 disabled:opacity-40 active:scale-95 transition-all flex items-center gap-2" data-id="element-7048" data-mp="1hknnhgopboh0">
              <RefreshCwIcon className={cn('w-4 h-4', loading && 'animate-spin')} data-id="element-7049" data-mp="2lg7c0n9aa423" />
              Actualizar
            </button>
            {canSeeMoney && <button onClick={handleToggleKitchen} disabled={togglingKitchen} aria-pressed={kitchenPaused} className={cn('h-10 px-4 rounded-xl border shadow-sm transition-all active:scale-[0.98] flex items-center gap-2', togglingKitchen && 'opacity-50 pointer-events-none', kitchenPaused ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-white border-gray-200 hover:bg-gray-50')} data-id="element-7050" data-mp="2hcsufs5p5fmj">
                <span className="text-sm font-medium text-gray-600" data-id="element-7051" data-mp="3suk6908da1qh">
                  Cocina:
                </span>
                {kitchenPaused ? <Badge variant="danger" className="flex items-center gap-1" data-id="element-7052" data-mp="1jkb2jfvtxzyk">
                    <PauseIcon className="w-3 h-3" data-id="element-7053" data-mp="1oc2v1vyjqfrc" /> Pausada
                  </Badge> : <Badge variant="success" className="flex items-center gap-1" data-id="element-7054" data-mp="1sdxv6x4qqn3j">
                    <PlayIcon className="w-3 h-3" data-id="element-7055" data-mp="vn5dxw62mp06" /> Activa
                  </Badge>}
              </button>}
          </div>
        </header>

        {toggleError && <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between gap-3" data-id="element-7056" data-mp="10o33itteulbp">
            <span data-id="element-7057" data-mp="19ktzl67yq06a">{toggleError}</span>
            <button onClick={() => setToggleError(null)} className="text-red-400 hover:text-red-600 text-xs font-bold" aria-label="Cerrar aviso" data-id="element-7058" data-mp="2g3igvnsa3ygi">
              ✕
            </button>
          </div>}

        <MetricsDateFilter startDate={startDate} endDate={endDate} onChange={(s, e) => {
        setStartDate(s);
        setEndDate(e);
      }} data-id="element-7059" data-mp="3mve9ei0ooo0c" />

        <div className={cn('grid grid-cols-1 gap-2', canSeeMoney ? 'sm:grid-cols-3' : 'sm:grid-cols-2')} data-id="element-7060" data-mp="k0m263weks8">
          <StatTile label="Pedidos" value={String(summary.count)} data-id="element-7061" data-mp="33zy0ir93eewd" />
          <StatTile label="A domicilio" value={String(summary.deliveries)} data-id="element-7062" data-mp="38xhh3xt9j3rh" />
          {canSeeMoney && <StatTile label="Total vendido" value={formatCurrency(summary.revenue)} data-id="element-7063" data-mp="3dwas9jl2uioh" />}
        </div>

        <div className="relative" data-id="element-7064" data-mp="3hzqkm3q45wvd">
          <SearchIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-7065" data-mp="t6v46wd8t82j" />
          <label htmlFor="web-orders-search" className="sr-only" data-id="element-7066" data-mp="x7f2425n4fpq">
            Buscar pedido
          </label>
          <input id="web-orders-search" type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por número, nombre o teléfono…" className="w-full pl-12 pr-12 h-12 rounded-xl border border-gray-200 bg-white text-sm font-medium focus:outline-none focus:border-gray-900" data-id="element-7067" data-mp="2hp62kbtnvdcc" />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400" aria-label="Limpiar búsqueda" data-id="element-7068" data-mp="1z1avfask0xs7">
              <XIcon className="w-4 h-4" data-id="element-7069" data-mp="1l0q8q3jiqhtf" />
            </button>}
        </div>

        {error && <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-7070" data-mp="3kc140o0vkgqu">
            {error}
          </div>}

        {readyError && <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" data-id="element-7071" data-mp="2bnk9uztv6u5">
            <AlertTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" data-id="element-7072" data-mp="3piplc4j7fdhh" />
            <span className="flex-1" data-id="element-7073" data-mp="3uh0v6f9i1nzp">{readyError}</span>
            <button onClick={() => setReadyError(null)} className="shrink-0 text-red-500 hover:text-red-700" aria-label="Cerrar aviso" data-id="element-7074" data-mp="3uhiwvrexkuf0">
              <XIcon className="w-4 h-4" data-id="element-7075" data-mp="q1hvk84i0nca" />
            </button>
          </div>}

        <section className="bg-white rounded-2xl border border-gray-200 p-3 min-h-[280px]" data-id="element-7076" data-mp="c1ynffbotbzz">
          {loading && orders.length === 0 ? <div className="flex justify-center py-16 text-gray-400" data-id="element-7077" data-mp="h47gscsh2g2m">
              <Loader2Icon className="w-6 h-6 animate-spin" data-id="element-7078" data-mp="2wsh6aljyt974" />
            </div> : visibleOrders.length === 0 ? <div className="text-center py-16" data-id="element-7079" data-mp="31q923qnqdfgl">
              <PackageIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" data-id="element-7080" data-mp="2u81ditm1lhmg" />
              <p className="font-bold text-gray-900" data-id="element-7081" data-mp="2p9zvmc0crock">
                {search ? 'Sin resultados para esa búsqueda' : 'No entraron pedidos web en este periodo'}
              </p>
              <p className="text-sm text-gray-500 mt-1" data-id="element-7082" data-mp="2y9wbpp8ofew0">
                {search ? 'Probá con otro número, nombre o teléfono.' : 'Probá ampliando el rango de fechas.'}
              </p>
            </div> : <ul className="divide-y divide-gray-100" data-id="element-7083" data-mp="26h5gni2qbw8c">
              {visibleOrders.map(order => {
            const voided = isVoidedStatus(order.status);
            const courierName = assignmentsByOrder.get(Number(order.id));
            const dispatched = Boolean(courierName);
            const isTakeaway = order.delivery_type === 'pickup' || order.delivery_type === 'dine_in';
            const done = isDoneStatus(order.status);
            const settled = voided || dispatched || isTakeaway && done;
            const mapsHref = order.maps_url || (order.delivery_lat && order.delivery_lng ? `https://www.google.com/maps/search/?api=1&query=${order.delivery_lat},${order.delivery_lng}` : null);
            return <li key={order.id} role="button" tabIndex={0} aria-label={`Ver detalle del pedido ${order.order_number || order.id}`} onClick={() => setDetailOrderId(String(order.id))} onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setDetailOrderId(String(order.id));
              }
            }} className={cn('py-3 px-2 flex flex-wrap items-start justify-between gap-3', 'cursor-pointer hover:bg-gray-50 rounded-lg transition-colors', 'focus:outline-none focus:ring-2 focus:ring-gray-900/20', voided && 'opacity-60',
            // Resueltos: se atenúan para que la atención quede
            // en los pedidos que todavía hay que despachar.
            !voided && settled && 'opacity-70')} data-id="element-7084" data-mp="2j7o7rkue0bzm">
                    <div className="min-w-0 flex-1" data-id="element-7085" data-mp="3w010wx3x7n0p">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-7086" data-mp="3qzn1g3gdxlea">
                        <span className="text-xs font-black text-gray-900 tabular-nums" data-id="element-7087" data-mp="3m122e7cvd7rf">
                          #{order.order_number || order.id}
                        </span>
                        <span className="text-xs text-gray-500 tabular-nums" data-id="element-7088" data-mp="2fiwu2h086nj3">
                          {fmtCR(order.created_at)}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-700" data-id="element-7089" data-mp="2c44ze8j82bxy">
                          {deliveryLabel(order.delivery_type)}
                        </span>
                        <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded', voided ? 'bg-red-100 text-red-700' : 'bg-blue-50 text-blue-700')} data-id="element-7090" data-mp="231m2s6614vk5">
                          {order.status || '—'}
                        </span>
                        {dispatched && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700" data-id="element-7091" data-mp="1oczjxuh71lo6">
                            Enviado · {courierName}
                          </span>}
                        {isTakeaway && done && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700" data-id="element-7092" data-mp="vr166yiaqogg">
                            Listo
                          </span>}
                      </div>
                      <p className="text-sm font-bold text-gray-900 truncate mt-0.5" data-id="element-7093" data-mp="15q8jjlp16j3k">
                        {order.customer_name || 'Sin nombre'}
                        {order.customer_phone && <span className="ml-2 text-xs font-medium text-gray-500 tabular-nums" data-id="element-7094" data-mp="1eoklukz9f5bu">
                            {order.customer_phone}
                          </span>}
                      </p>
                      {order.delivery_address && <p className="text-xs text-gray-500 flex items-start gap-1 mt-0.5" data-id="element-7095" data-mp="1jgc3g60fu0pu">
                          <MapPinIcon className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-7096" data-mp="1xfv6ikee6lva" />
                          {mapsHref ? <a href={mapsHref} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="underline hover:text-gray-900 break-words" data-id="element-7097" data-mp="1tg2x65h6cbov">
                              {order.delivery_address}
                            </a> : <span className="break-words" data-id="element-7098" data-mp="2h4159l871tmh">
                              {order.delivery_address}
                            </span>}
                        </p>}
                    </div>
                    <div className="flex items-center gap-3 shrink-0" data-id="element-7099" data-mp="2q1tmiuxh7bci">
                      {/* Recoger / Comer en el local: no hay repartidor,
                          así que se cierra marcándolo como listo. Queda
                          guardado en la base (orders.status='ready'). */}
                      {isTakeaway && !voided && !done && <button onClick={e => {
                  e.stopPropagation();
                  handleMarkReady(Number(order.id));
                }} disabled={markingReady === Number(order.id)} className="h-10 px-3 rounded-lg font-bold text-sm bg-gray-900 text-white hover:bg-gray-800 active:scale-95 transition-all disabled:opacity-40 flex items-center gap-2" data-id="element-7100" data-mp="1utrnto2us31t">
                          {markingReady === Number(order.id) ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-7101" data-mp="125vsamp0sh6m" /> : <CheckCircle2Icon className="w-4 h-4" data-id="element-7102" data-mp="y53jfuk28eav" />}
                          Marcar listo
                        </button>}
                      {canSeeMoney && <div className="text-right" data-id="element-7103" data-mp="o7p1fqihnhgg">
                          <p className="text-base font-black text-gray-900 tabular-nums" data-id="element-7104" data-mp="mxrcp2f2ip8s">
                            {formatCurrency(order.total)}
                          </p>
                          <p className="text-xs text-gray-500 tabular-nums" data-id="element-7105" data-mp="hwvdh25nvr9p">
                            Envío {formatCurrency(order.delivery_fee)}
                          </p>
                        </div>}
                    </div>
                  </li>;
          })}
            </ul>}
        </section>
      </main>

      {/* Detalle del pedido — solo lectura. No cambia estados ni imprime. */}
      <WebOrderDetailModal orderId={detailOrderId} isOpen={detailOrderId !== null} onClose={() => setDetailOrderId(null)} canSeeMoney={canSeeMoney} data-id="element-7106" data-mp="3qdyibeh1mwk7" />
    </AdminLayout>;
}
function StatTile({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="p-3 rounded-xl border border-gray-200 bg-white" data-id="element-7107" data-mp="3ceqdo6242w8m">
      <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide" data-id="element-7108" data-mp="1rwy1o54th4tm">
        {label}
      </p>
      <p className="text-lg font-black text-gray-900 tabular-nums mt-0.5" data-id="element-7109" data-mp="1vy9hxk1swdp5">
        {value}
      </p>
    </div>;
}